// 島の拡張パネル（v0.7.2）。HUD の「🪐 島」ボタンで開くモーダル。
//
// v0.7.1 では島の上に常時「✦ 新しい島（💰N）」ボタンが出ていたが、画面を散らかすので
// 廃止し、ここに格納した。各島のサムネ（簡易ディスク風のドット）と状態を一覧し、次の島の
// アンロック（費用提示 → 購入）をここでだけ行う。
//
// 購入演出は「今は軽い達成」（キラキラ＋効果音＋トースト）に留める方針（盛大な購入演出は
// 将来拡張。GAME_DESIGN.md の TODO 参照）。このモーダルは DOM 直組み・状態は持たず、
// main から渡された GameState で都度描画し、購入は onUnlock コールバックに委ねる。

import type { GameState } from './../sim/state';
import { MAX_ISLANDS } from './../sim/state';
import { islandUnlockCost, canUnlockNextIsland } from './../sim/islands';
import { shapeForIslandIndex, type IslandShape } from './../sim/garden';
import { islandSeason, SEASON_ICONS, SEASON_LABELS, type Season } from './../sim/season';
import { CUSTOMER_CATALOG } from './../sim/customers';

/** 季節 → その季節を好む客の名前（島が「呼ぶ常連」の説明に使う、v0.8.1）。 */
function seasonRegulars(season: Season): string {
  return CUSTOMER_CATALOG.filter((c) => c.favoriteSeason === season)
    .map((c) => c.name)
    .join('・');
}

/** 拡張島の季節・別館説明（「夏の別館 🌿・夏の常連が通う（ルナモス・パドル）」）。 */
function islandSeasonSub(index: number): string {
  const s = islandSeason(index);
  if (!s) return `眺めて育てる拡張島（${SHAPE_LABEL[shapeForIslandIndex(index)]}）`;
  const regulars = seasonRegulars(s);
  const who = regulars ? `${regulars}が通う` : 'その季節の常連が通う';
  return `${SEASON_LABELS[s]}の別館 ${SEASON_ICONS[s]}・${who}（${SHAPE_LABEL[shapeForIslandIndex(index)]}）`;
}

/** 島の形状を一目で示す絵文字（島選択セレクタのサムネに添える、v0.7.4）。 */
const SHAPE_ICON: Record<IslandShape, string> = {
  round: '⭕',
  square: '⬛',
  twin: '🌗',
  clover: '🍀',
  teardrop: '💧',
  oval: '🥚',
  hex: '⬡',
};

/** 島の形状の和名（サブ表示用、v0.7.4）。 */
const SHAPE_LABEL: Record<IslandShape, string> = {
  round: '丸い島',
  square: '四角い島',
  twin: '双子の島（橋つき）',
  clover: '四つ葉の島',
  teardrop: 'しずく形の島',
  oval: '楕円の島',
  hex: '六角の島',
};

export interface IslandModal {
  /** state と現在表示中の島 index で中身を描いて開く。 */
  open(state: GameState, viewIndex: number): void;
  close(): void;
  readonly isOpen: boolean;
}

export interface IslandModalOptions {
  modalEl: HTMLElement;
  bodyEl: HTMLElement;
  /** 「この島へ移動」: 一覧の島行タップで呼ぶ（モーダルを閉じて移動）。 */
  onGoto(index: number): void;
  /** 「次の島をアンロック」: 購入ボタンで呼ぶ（成否は main 側で判定・演出）。 */
  onUnlock(): void;
}

/**
 * 1 島ぶんのサムネ（簡易ディスク風）を作る（v0.7.4: 島の形状アイコンを表示）。
 * ホーム島はカフェ印（🏠）、拡張島はその島の形状アイコン（⬛ 🌗 🍀 💧 …）で「どんな形の島か」を示す。
 */
function islandThumb(index: number): HTMLElement {
  const t = document.createElement('div');
  const isHome = index === 0;
  t.className = 'isl-thumb' + (isHome ? ' home' : '');
  t.textContent = isHome ? '🏠' : SHAPE_ICON[shapeForIslandIndex(index)];
  return t;
}

export function createIslandModal(opts: IslandModalOptions): IslandModal {
  const { modalEl, bodyEl, onGoto, onUnlock } = opts;
  let open = false;

  function render(state: GameState, viewIndex: number): void {
    bodyEl.innerHTML = '';

    const lead = document.createElement('div');
    lead.className = 'isl-lead';
    lead.textContent =
      `島を選んでジャンプ。隣の島は「眺めて育てる庭」（形もそれぞれ違う）。庭デコの属性は全島まとめて本店の集客を後押しする。`;
    bodyEl.appendChild(lead);

    // アンロック済みの島一覧（タップでその島へカルーセル移動して閉じる＝ジャンプ用セレクタ）。
    for (let i = 0; i < state.unlockedIslands; i++) {
      const row = document.createElement('div');
      row.className = 'isl-row' + (i === viewIndex ? ' current' : '');
      row.appendChild(islandThumb(i));
      const info = document.createElement('div');
      info.className = 'isl-info';
      const nm = document.createElement('div');
      nm.className = 'nm';
      nm.textContent = i === 0 ? 'ホーム島' : `島 ${i + 1}`;
      const sub = document.createElement('div');
      sub.className = 'sub';
      sub.textContent =
        i === 0 ? 'カフェ・入口・客が来る本店（丸い島）' : islandSeasonSub(i);
      info.append(nm, sub);
      row.appendChild(info);
      if (i === viewIndex) {
        const badge = document.createElement('span');
        badge.className = 'isl-badge';
        badge.textContent = '表示中';
        row.appendChild(badge);
      }
      row.addEventListener('click', () => {
        onGoto(i);
        close();
      });
      bodyEl.appendChild(row);
    }

    // 次の島（未アンロック）: 費用提示 → 購入。最大まで開いていれば「全部開いた」表示。
    if (state.unlockedIslands < MAX_ISLANDS) {
      const next = document.createElement('div');
      next.className = 'isl-row ghost';
      const thumb = document.createElement('div');
      thumb.className = 'isl-thumb ghostThumb';
      thumb.textContent = '✦';
      next.appendChild(thumb);
      const info = document.createElement('div');
      info.className = 'isl-info';
      const cost = islandUnlockCost(state.unlockedIslands);
      const nm = document.createElement('div');
      nm.className = 'nm';
      const nextSeason = islandSeason(state.unlockedIslands);
      nm.textContent = nextSeason
        ? `新しい島（${SEASON_LABELS[nextSeason]}の別館 ${SEASON_ICONS[nextSeason]}）`
        : `新しい島（島 ${state.unlockedIslands + 1}）`;
      const sub = document.createElement('div');
      sub.className = 'sub';
      sub.textContent = nextSeason
        ? `${seasonRegulars(nextSeason)}を一年じゅう呼べるようになる`
        : `霧の向こうに、まだ庭を広げる余地がある`;
      info.append(nm, sub);
      next.appendChild(info);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'isl-unlock pixel-btn';
      btn.textContent = `💰${cost} で広げる`;
      btn.disabled = !canUnlockNextIsland(state);
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        onUnlock();
      });
      next.appendChild(btn);
      bodyEl.appendChild(next);
    } else {
      const done = document.createElement('div');
      done.className = 'isl-done';
      done.textContent = `すべての島（${MAX_ISLANDS} 島）が広がった 🌌`;
      bodyEl.appendChild(done);
    }
  }

  function openModal(state: GameState, viewIndex: number): void {
    render(state, viewIndex);
    modalEl.classList.add('open');
    open = true;
  }
  function close(): void {
    modalEl.classList.remove('open');
    open = false;
  }

  // 背景スクリム or とじるボタンで閉じる。
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl || (e.target as HTMLElement).dataset?.close != null) close();
  });

  return {
    open: openModal,
    close,
    get isOpen() {
      return open;
    },
  };
}
